import * as React from 'react';

import { Skeleton } from './Skeleton';
import { Text } from './Typography';

// Closed table primitive — caller passes column definitions and a row
// dataset; primitive owns the structure (<table>, <thead>, <tbody>,
// <tfoot>) and the visual chrome (zebra striping, header style,
// borders, hover paint).
//
// Column shape:
//   { key, header, align?: 'start'|'center'|'end', render?: (row) => ReactNode }
// `key` is the property read from each row when `render` is absent and
// is also used as the React key when iterating columns. `header` is
// rendered inside <Text variant="overline">. `align` defaults to
// 'start' and applies to both the header cell and the data cells in
// that column. `render(row)` lets callers slot custom content
// (badges, switches, links) per cell.
//
// Other props:
//   rows, emptyMessage (required).
//   rowKey — string property name read from each row to derive the
//     React key. Defaults to 'id'. Override only when rows don't
//     carry an `id` field.
//   loading — when true, renders 5 skeleton rows in place of the data.
//   footer — optional ReactNode rendered as a single full-width cell
//     in <tfoot>. For column-aligned totals we'd extend the API; today
//     a single horizontal strip covers the simple summary case.
//   onRowClick — optional. When wired, rows get cursor-pointer + a
//     hover paint. Without it rows are static.
//
// Striping: rows are painted explicitly — odd rows `bg-background`,
// even rows `bg-muted` — instead of leaving odd rows transparent and
// painting only even rows. The transparent variant inherited the
// parent surface's tint and blended the stripe away whenever the
// table sat inside a muted/tinted container (catalog preview, page
// background). The header is painted `bg-muted` so it doesn't collide
// with the first data row (which is always painted `bg-background`)
// and reads as a distinct band.
//
// Loading skeletons compose `<Skeleton tone="strong" />` — table cells
// alternate between `bg-background` and `bg-muted`, so the subtle
// (`bg-muted`) tone would disappear on muted rows. `strong`
// (`bg-border`) reads on both.
//
// Defensive overrides: the legacy DataTables stylesheet
// (`src/css/ui_app.css`) carries a global `table tbody tr { cursor:
// pointer; line-height: 1.8em; font-size: 0.8333em }`. The primitive
// nullifies all three on each row so its own behaviour is
// deterministic. The cleaner long-term fix is to scope or drop that
// legacy rule (tracked in DESIGN_SYSTEM_REVIEW.md).

const SKELETON_ROW_COUNT = 5;

const ALIGN_CLASSNAME = {
    start: 'tw-text-left',
    center: 'tw-text-center',
    end: 'tw-text-right'
};

const HEAD_CLASSNAME = 'tw-h-12 tw-px-4 tw-align-middle tw-font-medium tw-text-muted-foreground';
const CELL_CLASSNAME = 'tw-p-4 tw-align-middle tw-text-sm tw-leading-normal';
const ROW_RESET_CLASSNAME = 'tw-text-sm tw-leading-normal';
const ROW_BORDER_CLASSNAME = 'tw-border-b tw-border-solid tw-border-border tw-transition-colors';
const ROW_ODD_CLASSNAME = 'tw-bg-background';
const ROW_EVEN_CLASSNAME = 'tw-bg-muted';
const ROW_INTERACTIVE_CLASSNAME = 'tw-cursor-pointer hover:tw-bg-secondary-surface';
const ROW_STATIC_CLASSNAME = 'tw-cursor-default';

const composeRowClassName = (isEven, isInteractive) => [
    ROW_RESET_CLASSNAME,
    ROW_BORDER_CLASSNAME,
    isEven ? ROW_EVEN_CLASSNAME : ROW_ODD_CLASSNAME,
    isInteractive ? ROW_INTERACTIVE_CLASSNAME : ROW_STATIC_CLASSNAME
].join(' ');

const renderHeaderCell = column => (
    <th
        key={column.key}
        className={`${HEAD_CLASSNAME} ${ALIGN_CLASSNAME[column.align ?? 'start']}`}
    >
        <Text tone="inherit" variant="overline">{column.header}</Text>
    </th>
);

const renderDataCell = (column, row) => {
    const content = column.render ? column.render(row) : row[column.key];

    return (
        <td
            key={column.key}
            className={`${CELL_CLASSNAME} ${ALIGN_CLASSNAME[column.align ?? 'start']}`}
        >
            {content}
        </td>
    );
};

const renderSkeletonRow = (columns, rowIndex) => (
    <tr key={`skeleton-${rowIndex}`} className={composeRowClassName(rowIndex % 2 === 1, false)}>
        {columns.map(column => (
            <td key={column.key} className={CELL_CLASSNAME}>
                <div className="tw-h-4 tw-w-full">
                    <Skeleton tone="strong" />
                </div>
            </td>
        ))}
    </tr>
);

const Table = ({
    columns,
    emptyMessage,
    footer,
    loading = false,
    onRowClick,
    rowKey = 'id',
    rows
}) => {
    const isInteractive = typeof onRowClick === 'function';

    let body;

    if (loading) {
        body = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => renderSkeletonRow(columns, index));
    } else if (rows.length === 0) {
        body = (
            <tr className={`${ROW_RESET_CLASSNAME} ${ROW_ODD_CLASSNAME}`}>
                <td className={`${CELL_CLASSNAME} tw-text-center`} colSpan={columns.length}>
                    <Text tone="muted" variant="body">{emptyMessage}</Text>
                </td>
            </tr>
        );
    } else {
        body = rows.map((row, index) => (
            <tr
                key={row[rowKey]}
                className={composeRowClassName(index % 2 === 1, isInteractive)}
                onClick={isInteractive ? () => onRowClick(row) : undefined}
            >
                {columns.map(column => renderDataCell(column, row))}
            </tr>
        ));
    }

    return (
        <div className="tw-relative tw-w-full tw-overflow-auto">
            <table className="tw-min-w-full tw-caption-bottom tw-text-sm">
                <thead className="tw-border-b tw-border-solid tw-border-border">
                    <tr className="tw-bg-muted">
                        {columns.map(renderHeaderCell)}
                    </tr>
                </thead>
                <tbody>
                    {body}
                </tbody>
                {footer ? (
                    <tfoot>
                        <tr>
                            <td
                                className={`${CELL_CLASSNAME} tw-border-t tw-border-solid tw-border-border tw-bg-muted tw-font-medium`}
                                colSpan={columns.length}
                            >
                                {footer}
                            </td>
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    );
};

export { Table };
